import { Intent } from '@blueprintjs/core';
import { MimeType } from 'vet-bones/constants';

import {
  databasesActionCreators,
  databasesStardogRequestDispatchers,
} from 'src/actions/databases/actionCreators';
import { DatabasesActionType } from 'src/actions/databases/actionTypes';
import { updateDatabaseReasoningSchemas } from 'src/actions/projects/action-creators/publishProject/updateDatabaseReasoningSchemas';
import { CreationType } from 'src/containers/PublishProjectDrawer/types';
import { databaseSelector } from 'src/selectors/database';
import { AppDispatch } from 'src/store/store';
import { ReduxStateGetter } from 'src/store/types';
import { readFile } from 'src/utils/readFile';

export const addModelFileToDatabase =
  ({
    databaseId,
    file,
    modelName,
    namedGraphIri,
  }: {
    databaseId: string;
    file: File;
    modelName: string;
    namedGraphIri: string;
  }) =>
  async (dispatch: AppDispatch, getState: ReduxStateGetter) => {
    const state = getState();
    const database = databaseSelector(databaseId)(state);

    if (!database) {
      return false;
    }

    dispatch(databasesActionCreators.addModelFileToDatabaseStarted(databaseId));

    // start a transaction
    const transactionResponse =
      await databasesStardogRequestDispatchers.beginDatabaseTransaction(
        databaseId
      );

    const transactionId = transactionResponse.payload.response?.body;

    if (
      transactionResponse.type ===
        DatabasesActionType.BEGIN_DATABASE_TRANSACTION_FAILURE ||
      !transactionId
    ) {
      dispatch(
        databasesActionCreators.addModelFileToDatabaseFinished({
          databaseId,
          intent: Intent.DANGER,
        })
      );
      return false;
    }

    // because Voicebox enabled databases do not allow adding non-empty
    // graphs to the schema via changing db options (`reasoning.schemas`),
    // we must update db options before creating & adding data to the models
    const updateDatabaseReasoningSchemasResponse =
      await updateDatabaseReasoningSchemas({
        database,
        dangling: {},
        modelInfos: [
          {
            creationType: CreationType.NEW,
            modelName,
            namedGraphIri,
            projectModelId: '',
            shouldPublish: true,
          },
        ],
      });

    if (!updateDatabaseReasoningSchemasResponse.success) {
      dispatch(
        databasesActionCreators.addModelFileToDatabaseFinished({
          databaseId,
          intent: Intent.DANGER,
        })
      );
      return false;
    }

    const contents = await readFile(file);
    const addDataResponse =
      await databasesStardogRequestDispatchers.addDataToDatabase({
        contents,
        databaseId,
        mimeType: MimeType.TURTLE,
        namedGraph: namedGraphIri,
        transactionId,
      });

    if (
      addDataResponse.type === DatabasesActionType.ADD_DATA_TO_DATABASE_FAILURE
    ) {
      dispatch(
        databasesActionCreators.addModelFileToDatabaseFinished({
          databaseId,
          intent: Intent.DANGER,
        })
      );
      return false;
    }

    const commitResponse =
      await databasesStardogRequestDispatchers.commitDatabaseTransaction(
        databaseId,
        transactionId
      );

    if (
      commitResponse.type ===
      DatabasesActionType.COMMIT_DATABASE_TRANSACTION_FAILURE
    ) {
      await databasesStardogRequestDispatchers.rollbackDatabaseTransaction(
        databaseId,
        transactionId
      );
      dispatch(
        databasesActionCreators.addModelFileToDatabaseFinished({
          databaseId,
          intent: Intent.DANGER,
        })
      );
      return false;
    }

    dispatch(
      databasesActionCreators.addModelFileToDatabaseFinished({
        databaseId,
        intent: Intent.SUCCESS,
      })
    );
    return true;
  };
